import type { Fighter, Scene } from './types';

// gunfire sfx keyed by weapon
export const SHOT_SFX: Record<string, string> = { ak: 'shot_ak', m4: 'shot_m4', lmg: 'shot_lmg', awp: 'shot_awp' };

// auto weapons fire a visual BURST per logical shot — the sim still resolves ONE
// shot (one damage number); only the muzzle flash / tracer / sound rattle. `gap`
// is wall-clock seconds between burst rounds.
export const BURST: Record<string, { count: number; gap: number }> = {
  awp: { count: 1, gap: 0 },
  rifle: { count: 2, gap: 0.15 },
  lmg: { count: 3, gap: 0.14 },
};

// Event `t` is an integer engine TICK index; these constants are purely visual
// pacing for on-screen replay and never affect the sim's combat outcome.
const SECONDS_PER_TICK = 0.01;
const ANIM_STRETCH = 4.8;
export const ROUND_ANIM = 12; // max seconds to replay a round's burst of events
export const playTime = (tick: number) => Math.min(ROUND_ANIM, tick * SECONDS_PER_TICK * ANIM_STRETCH);

// match-start intro: fighters walk in from their own side before round 1 fires.
// ENTER_DUR delays the first round's events so the entrance plays clean first.
export const ENTER_DUR = 0.9;
export const GREN_FLIGHT = 0.5;
export const STANCE_STEP: Record<string, number> = { attack: 0, maneuver: 40, defense: -40 };
export const KNIFE_TRAVEL_FRAC = 0.85; // how far toward the target the lunge closes
export const KNIFE_DEATH_FRAC = 0.3;   // where a killed knifer's corpse drops (toward own side)
export const KNIFE_LUNGE = 0.7;

export const now = () => performance.now() / 1000;

export const cull = <T extends { t0: number; life: number }>(list: T[], n: number): void => {
  for (let i = list.length - 1; i >= 0; i--) if (n - list[i].t0 >= list[i].life) list.splice(i, 1);
};

/** Nearest ALIVE enemy of `f` (opposite team) in the current scene, or null — used to keep
 *  every barrel trained on a target instead of idling forward. Uses the live (animated) state,
 *  so it re-targets as bodies fall during the replay. */
export function nearestEnemyId(sc: Scene, f: Fighter): string | null {
  let best: string | null = null;
  let bestD = Infinity;
  for (const e of sc.fighters) {
    if (e.dead || e.team === f.team) continue;
    const dx = e.x - f.x, dy = e.y - f.y, d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = e.id; }
  }
  return best;
}

// Combat sfx that aren't about YOU (you're neither the actor nor a target of the event)
// play a little quieter — your own shots/hits/throws stay full, the rest of the firefight
// recedes so your POV reads clearly over a 5v5.
const OTHERS_VOL = 0.75;
export type Mix = (base: number, ...involved: (string | undefined | null)[]) => number;
export const createMix = (getYouId: () => string | null): Mix => (base, ...involved) => {
  const you = getYouId();
  return you && involved.includes(you) ? base : base * OTHERS_VOL;
};
